import type { AppointmentSlotDate } from '../types/appointment.types';

export const MONTHS_SHORT_EN = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export const MONTHS_SHORT_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
] as const;

export const MONTHS_LONG_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const MONTHS_LONG_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
] as const;

/** Default number of client-generated slot dates (until dates come from API). */
export const DEFAULT_SLOT_DATE_COUNT = 15;

/** Default number of months to show slot dates ahead of today. */
export const DEFAULT_SLOT_MONTHS = 3;

/**
 * When profile-details sends a start date and no usable end date,
 * show this many calendar days beginning on that start date.
 */
export const BOOKING_START_ONLY_DAY_COUNT = 90;

/**
 * Returns the number of days from today (inclusive) to exactly `months` calendar
 * months later (inclusive end day), correctly handling months with 28/29/30/31 days.
 *
 * Example: today = Sept 16  →  end = Dec 16  →  returns 92 days
 */
export function getSlotDayCount(months: number, from?: Date): number {
  const start = startOfDay(from ?? new Date());
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  // Diff in ms → days (inclusive of start day)
  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Accept a calendar date from profile-details.
 * Uses the `YYYY-MM-DD` prefix so a time suffix cannot shift the day.
 * Empty, missing, and impossible dates (e.g. 2026-02-31) are unusable.
 */
export function parseBookingCalendarDate(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return toIsoDate(date);
}

/** Local calendar date for a `YYYY-MM-DD` value. */
export function isoToLocalDate(isoDate: string): Date | null {
  const parsed = parseBookingCalendarDate(isoDate);
  if (!parsed) {
    return null;
  }
  const [year, month, day] = parsed.split('-').map(Number);
  if (year == null || month == null || day == null) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function listDaysFrom(start: Date, count: number): string[] {
  if (count <= 0) {
    return [];
  }
  const origin = startOfDay(start);
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(origin);
    date.setDate(origin.getDate() + index);
    return toIsoDate(date);
  });
}

function inclusiveDayCount(startIso: string, endIso: string): number {
  if (startIso > endIso) {
    return 0;
  }
  const start = isoToLocalDate(startIso);
  const end = isoToLocalDate(endIso);
  if (!start || !end) {
    return 0;
  }
  const diffMs = startOfDay(end).getTime() - startOfDay(start).getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

export interface ResolveAppointmentBookingDatesOptions {
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
  today?: Date | undefined;
  /**
   * Used only when both bounds are missing or invalid.
   * Ignored once either bound is a real calendar date.
   */
  count?: number | undefined;
  months?: number | undefined;
}

/**
 * Dates shown in the book-appointment strip.
 * 1. Both bounds missing/invalid → today through `months` (or `count`) days.
 * 2. Start only → `BOOKING_START_ONLY_DAY_COUNT` days from that start.
 *    If that start is already before today, the strip starts at today and
 *    ends on the last day of that window. A window that has fully passed
 *    is empty.
 * 3. End only → today through the end date.
 * 4. Both valid, start on or before the end date → that inclusive range.
 *    If the start date is already before today and the end date is still
 *    today or later, the strip starts at today.
 *    If both dates are before today, the strip is empty.
 * 5. Start date after the end date → same as start only (90 days from the start).
 */
export function resolveAppointmentBookingDateIsos(
  options: ResolveAppointmentBookingDatesOptions = {},
): string[] {
  const today = startOfDay(options.today ?? new Date());
  const start = parseBookingCalendarDate(options.startDate);
  const end = parseBookingCalendarDate(options.endDate);

  if (!start && !end) {
    const count =
      options.months != null
        ? getSlotDayCount(options.months, today)
        : (options.count ?? DEFAULT_SLOT_DATE_COUNT);
    return listDaysFrom(today, count);
  }

  if (start && (!end || start > end)) {
    return listStartOnlyBookingDates(start, today);
  }

  if (!start && end) {
    return listDaysFrom(today, inclusiveDayCount(toIsoDate(today), end));
  }

  return listBoundedBookingDates(start, end, today);
}

/**
 * True when the book-appointment action should be hidden because the
 * profile-details window is already over:
 * - both dates are before today, and the start is on or before the end
 * - only a start date, or the start is after the end, and that 90-day window has ended
 * - only an end date, and that end date is before today
 */
export function isAppointmentBookingWindowClosed(
  startDate: unknown,
  endDate: unknown,
  today?: Date,
): boolean {
  const start = parseBookingCalendarDate(startDate);
  const end = parseBookingCalendarDate(endDate);
  const todayDate = startOfDay(today ?? new Date());
  const todayIso = toIsoDate(todayDate);

  if (start && (!end || start > end)) {
    return listStartOnlyBookingDates(start, todayDate).length === 0;
  }
  if (!start && end) {
    return end < todayIso;
  }
  if (start && end) {
    return start < todayIso && end < todayIso;
  }
  return false;
}

function listStartOnlyBookingDates(start: string, today: Date): string[] {
  const startDate = isoToLocalDate(start);
  if (!startDate) {
    return [];
  }
  const window = listDaysFrom(startDate, BOOKING_START_ONLY_DAY_COUNT);
  const windowEnd = window.at(-1);
  if (!windowEnd) {
    return [];
  }
  return listBoundedBookingDates(start, windowEnd, today);
}

function listBoundedBookingDates(
  start: string | null,
  end: string | null,
  today: Date,
): string[] {
  if (!start || !end) {
    return [];
  }
  const todayIso = toIsoDate(today);
  if (end < todayIso) {
    return [];
  }
  const windowStartIso = start < todayIso ? todayIso : start;
  const rangeStart = isoToLocalDate(windowStartIso);
  if (!rangeStart) {
    return [];
  }
  return listDaysFrom(rangeStart, inclusiveDayCount(windowStartIso, end));
}

/**
 * Client-generated slot dates for UI (dates not from API yet).
 * Relative to "today". Used by web + native.
 */
export function buildSlotDates(
  count = DEFAULT_SLOT_DATE_COUNT,
  options?: { 
    disablePastInclusive?: boolean;
    isRTL?: boolean;
    todayLabel?: string;
    tomorrowLabel?: string;
    /**
     * When provided, overrides `count` with the exact number of days spanning
     * this many calendar months from today (handles 30/31-day month boundaries).
     * Ignored when a usable booking start or end date is provided.
     */
    months?: number | undefined;
    /** profile-details `appointmentBookingStartDate` */
    bookingStartDate?: string | null | undefined;
    /** profile-details `appointmentBookingEndDate` */
    bookingEndDate?: string | null | undefined;
    today?: Date | undefined;
  },
): AppointmentSlotDate[] {
  const today = startOfDay(options?.today ?? new Date());
  const isoDates = resolveAppointmentBookingDateIsos({
    startDate: options?.bookingStartDate,
    endDate: options?.bookingEndDate,
    today,
    count,
    months: options?.months,
  });
  const disablePast = options?.disablePastInclusive === true;
  const isRTL = options?.isRTL === true;
  const todayLabel = options?.todayLabel ?? 'Today';
  const tomorrowLabel = options?.tomorrowLabel ?? 'Tomorrow';
  const monthsShort = isRTL ? MONTHS_SHORT_AR : MONTHS_SHORT_EN;
  const todayIso = toIsoDate(today);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowIso = toIsoDate(tomorrow);

  return isoDates.flatMap((iso) => {
    const date = isoToLocalDate(iso);
    if (!date) {
      return [];
    }
    const sublabel = slotDateSublabel(iso, todayIso, tomorrowIso, todayLabel, tomorrowLabel);
    const disabled = disablePast && (iso === todayIso || iso === tomorrowIso);
    return [{
      id: iso,
      value: iso,
      label: `${date.getDate()} ${monthsShort[date.getMonth()]}`,
      ...(sublabel ? { sublabel } : {}),
      ...(disabled ? { disabled: true } : {}),
    }];
  });
}

function slotDateSublabel(
  iso: string,
  todayIso: string,
  tomorrowIso: string,
  todayLabel: string,
  tomorrowLabel: string,
): string | undefined {
  if (iso === todayIso) {
    return todayLabel;
  }
  if (iso === tomorrowIso) {
    return tomorrowLabel;
  }
  return undefined;
}

export function formatLongDate(isoDate: string, isRTL?: boolean): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const monthsLong = isRTL ? MONTHS_LONG_AR : MONTHS_LONG_EN;
  const month = monthsLong[m - 1] ?? '';
  return `${d} ${month} ${y}`;
}

/** Short label e.g. "5 Aug" from ISO YYYY-MM-DD. */
export function formatShortDate(isoDate: string, isRTL?: boolean): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const monthsShort = isRTL ? MONTHS_SHORT_AR : MONTHS_SHORT_EN;
  const month = monthsShort[m - 1] ?? '';
  return `${d} ${month}`;
}
